import React, { useState, useRef } from 'react';
import { format } from 'date-fns';
import { HouseHeart, Droplets, ChevronDown, RotateCcw, Milk, Angry, Frown, Meh, Smile, Laugh, HandHelping, BookOpen } from 'lucide-react';
import { Bowl1, Bowl2, Bowl3 } from './Custom Icons/BowlIcons';
import { cn } from '../lib/utils';
import { useLanguage } from '../functions/useLanguage';
import { DiaryEditor } from './DiaryEditor';
import type { DiaryEntry } from '../lib/diary';

export type MucousMembraneColor = 'pink' | 'red' | 'blue' | 'white';

interface TodayToolbarProps {
  isMetric: boolean;
  // Fluid intake
  onAddFluid: (amount: number) => void;
  onUndoFluid?: () => void;
  totalFluidIn: number;
  fluidChipCounts?: Record<number, number>;
  // Temperature (in wellness panel)
  onSaveTemp: (tempC: number) => void;
  onUndoTemp?: () => void;
  todayTemp: number | null;
  // Mucous Membranes
  onSaveMucousMembrane: (color: MucousMembraneColor) => void;
  todayMucousMembrane: MucousMembraneColor | null;
  // Food intake
  onAddFood: (amount: number) => void;
  onUndoFood?: () => void;
  totalFoodIn: number;
  foodChipCounts?: Record<number, number>;
  // Observations
  observationsMood: number | null;
  setObservationsMood: (v: number | null) => void;
  observationsInput: string;
  setObservationsInput: (v: string) => void;
  onSaveObservations: () => void;
  onUndoObservations?: () => void;
  observationsLog: any;
  // Diary
  diaryEntries: DiaryEntry[];
  onDiarySave: (html: string, mood: number | null) => void;
  petName: string;
}

const FLUID_CHIPS = [
  { ml: 500,  metricLabel: '500 ml', imperialLabel: '17 fl oz',  icon: <Milk size={16} /> },
  { ml: 1000, metricLabel: '1 L',    imperialLabel: '34 fl oz',  icon: <Bowl1 width={20} height={20} /> },
  { ml: 1500, metricLabel: '1.5 L',  imperialLabel: '51 fl oz',  icon: <Bowl2 width={20} height={20} /> },
  { ml: 3000, metricLabel: '3 L',    imperialLabel: '101 fl oz', icon: <Bowl3 width={20} height={20} /> },
];

const MM_OPTIONS: { value: MucousMembraneColor; label: string; dotClass: string; activeClass: string }[] = [
  { value: 'pink',  label: 'Pink',  dotClass: 'bg-pink-400',  activeClass: 'bg-pink-400 border-white text-white' },
  { value: 'red',   label: 'Red',   dotClass: 'bg-red-500',   activeClass: 'bg-red-500 border-white text-white'  },
  { value: 'blue',  label: 'Blue',  dotClass: 'bg-blue-500',  activeClass: 'bg-blue-500 border-white text-white' },
  { value: 'white', label: 'White', dotClass: 'bg-white border border-gray-300', activeClass: 'bg-white border-white text-gray-700' },
];

const MOODS = [
  <Angry size={22} />,
  <Frown size={22} />,
  <Meh size={22} />,
  <Smile size={22} />,
  <Laugh size={22} />,
] as const;

const FOOD_CHIPS = [
  { mg: 25,   metricLabel: '25 g',   imperialLabel: '0.9 oz',  icon: <HandHelping size={16} /> },
  { mg: 100,  metricLabel: '100 g',  imperialLabel: '3.5 oz',  icon: <Bowl1 width={20} height={20} /> },
  { mg: 250,  metricLabel: '250 g',  imperialLabel: '8.8 oz',  icon: <Bowl2 width={20} height={20} /> },
  { mg: 500,  metricLabel: '500 g',  imperialLabel: '17.6 oz', icon: <Bowl3 width={20} height={20} /> },
];


export function TodayToolbar({
  isMetric,
  onAddFluid, onUndoFluid, totalFluidIn, fluidChipCounts,
  onAddFood, onUndoFood, totalFoodIn, foodChipCounts,
  onSaveTemp, onUndoTemp, todayTemp,
  onSaveMucousMembrane, todayMucousMembrane,
  observationsMood, setObservationsMood, observationsInput, setObservationsInput,
  onSaveObservations, onUndoObservations, observationsLog,
  diaryEntries, onDiarySave, petName,
}: TodayToolbarProps) {
  const [wellnessOpen, setWellnessOpen] = useState(false);
  const [diaryOpen, setDiaryOpen]       = useState(false);
  const [diaryInput, setDiaryInput]     = useState('');
  const [diaryMood, setDiaryMood]       = useState<number | null>(null);
  const [customFluid, setCustomFluid]   = useState('');
  const [customFood, setCustomFood]     = useState('');
  const [tempToolbarInput, setTempToolbarInput] = useState('');
  const obsTextRef = useRef<HTMLTextAreaElement>(null);
  const { t } = useLanguage();

  const handleDiarySave = () => {
    if (!diaryInput || diaryInput === '<p></p>') return;
    onDiarySave(diaryInput, diaryMood);
    setDiaryInput('');
    setDiaryMood(null);
  };

  const toggle = (panel: 'wellness' | 'diary') => {
    if (panel === 'wellness') {
      const opening = !wellnessOpen;
      setWellnessOpen(o => !o);
      setDiaryOpen(false);
      if (opening) {
        setObservationsInput(observationsLog?.observations_text || '');
        setObservationsMood(observationsLog?.mood !== undefined ? observationsLog.mood : null);
      }
    } else {
      setDiaryOpen(o => !o);
      setWellnessOpen(false);
    }
  };

  const handleCustomFluid = () => {
    const v = parseInt(customFluid);
    if (v > 0) { onAddFluid(v); setCustomFluid(''); }
  };

  const handleCustomFood = () => {
    const v = parseInt(customFood);
    if (v > 0) { onAddFood(v); setCustomFood(''); }
  };

  const handleSaveTemp = () => {
    const v = parseFloat(tempToolbarInput);
    if (v > 0) {
      const tempC = isMetric ? v : (v - 32) * 5 / 9;
      onSaveTemp(tempC);
      setTempToolbarInput('');
    }
  };

  return (
    <div className="bg-white overflow-hidden">

      {/* ── Wellness Check expansion panel ── */}
      {wellnessOpen && (
        <div className="px-4 pt-4 pb-3 space-y-4 bg-primary-brown border-b border-white/20">

          {/* ① Mucous Membranes */}
          <div>
            <p className="text-xs font-bold text-white/80 uppercase tracking-widest mb-2 truncate">{t('records.mucousMembranes')}</p>
            <div className="flex gap-2">
              {MM_OPTIONS.map(({ value, label, dotClass, activeClass }) => (
                <button
                  key={value}
                  onClick={() => onSaveMucousMembrane(value)}
                  className={cn(
                    'flex-1 py-2 rounded-xl text-xs font-bold border-2 transition-all flex items-center justify-center',
                    todayMucousMembrane === value
                      ? `${activeClass} scale-105 shadow-md`
                      : 'bg-white/15 border-white/30 text-white hover:bg-white/25'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* ② Temperature */}
          <div className="pt-1 border-t border-white/20">
            <p className="text-xs font-bold text-white/80 uppercase tracking-widest mb-2 truncate">{t('records.temperature')} ({isMetric ? '°C' : '°F'})</p>
            {todayTemp ? (
              <div className="flex items-center justify-between bg-white/20 rounded-xl px-3 py-2">
                <span className="text-white font-semibold text-sm">
                  {isMetric ? `${todayTemp.toFixed(1)} °C` : `${(todayTemp * 9/5 + 32).toFixed(1)} °F`}
                </span>
                {onUndoTemp && (
                  <button onClick={onUndoTemp} className="p-1 hover:bg-white/20 rounded-full text-white/70 transition-colors" title="Undo temperature entry">
                    <RotateCcw size={14} />
                  </button>
                )}
              </div>
            ) : (
              <div className="flex gap-2 items-center">
                <input
                  type="number"
                  inputMode="decimal"
                  placeholder={isMetric ? '38.5' : '101.3'}
                  value={tempToolbarInput}
                  onChange={e => setTempToolbarInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSaveTemp()}
                  className="flex-1 bg-white/20 border border-white/30 rounded-xl px-3 py-2 text-sm text-white placeholder:text-white/60 outline-none focus:bg-white/30 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <button
                  onClick={handleSaveTemp}
                  disabled={!tempToolbarInput}
                  className="px-3 py-2 bg-white/25 text-white rounded-xl text-sm font-semibold disabled:opacity-40 hover:bg-white/35"
                >
                  {t('records.toolbar.save')}
                </button>
              </div>
            )}
          </div>

          {/* ③ Water Intake */}
          <div className="pt-1 border-t border-white/20">
            <p className="text-xs font-bold text-white/80 uppercase tracking-widest mb-2 truncate">{t('records.fluidIntake')}</p>
            <div className="relative overflow-visible">
              <div className="flex gap-2 overflow-x-auto overflow-y-visible scrollbar-hide pb-3 pt-2 -mt-2">
                {FLUID_CHIPS.map(({ ml, metricLabel, imperialLabel, icon }) => {
                  const count = fluidChipCounts?.[ml] ?? 0;
                  return (
                    <button
                      key={ml}
                      onClick={() => onAddFluid(ml)}
                      className="relative flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 text-white text-sm font-semibold border border-white/30 active:scale-95 transition-transform hover:bg-white/30"
                    >
                      <span className="opacity-90">{icon}</span>
                      +{isMetric ? metricLabel : imperialLabel}
                      {count > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-white text-primary-brown text-[10px] font-bold px-1 leading-none shadow">
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-primary-brown/80 to-transparent" />
            </div>
            <div className="flex gap-2 items-center mt-2">
              <input
                type="number"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Custom value"
                value={customFluid}
                onChange={e => setCustomFluid(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCustomFluid()}
                className="flex-1 bg-white/20 border border-white/30 rounded-xl px-3 py-2 text-sm text-white placeholder:text-white/60 outline-none focus:bg-white/30 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <button
                onClick={handleCustomFluid}
                disabled={!customFluid}
                className="px-3 py-2 bg-white/25 text-white rounded-xl text-sm font-semibold disabled:opacity-40 hover:bg-white/35"
              >
                {t('records.toolbar.add')}
              </button>
              {totalFluidIn > 0 && onUndoFluid && (
                <button onClick={onUndoFluid} className="p-2 hover:bg-white/20 rounded-full text-white/70 transition-colors" title="Undo last fluid entry">
                  <RotateCcw size={16} />
                </button>
              )}
            </div>
          </div>

          {/* ④ Food Intake */}
          <div className="pt-1 border-t border-white/20">
            <p className="text-xs font-bold text-white/80 uppercase tracking-widest mb-2 truncate">{t('records.foodIntake')}</p>
            <div className="relative overflow-visible">
              <div className="flex gap-2 overflow-x-auto overflow-y-visible scrollbar-hide pb-3 pt-2 -mt-2">
                {FOOD_CHIPS.map(({ mg, metricLabel, imperialLabel, icon }) => {
                  const count = foodChipCounts?.[mg] ?? 0;
                  const label = isMetric ? metricLabel : imperialLabel;
                  return (
                    <button
                      key={mg}
                      onClick={() => onAddFood(mg)}
                      className="relative flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 text-white text-sm font-semibold border border-white/30 active:scale-95 transition-transform hover:bg-white/30"
                    >
                      <span className="opacity-90">{icon}</span>
                      +{label}
                      {count > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-white text-primary-brown text-[10px] font-bold px-1 leading-none shadow">
                          {count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-primary-brown/80 to-transparent" />
            </div>
            <div className="flex gap-2 items-center mt-2">
              <input
                type="number"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Custom value"
                value={customFood}
                onChange={e => setCustomFood(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCustomFood()}
                className="flex-1 bg-white/20 border border-white/30 rounded-xl px-3 py-2 text-sm text-white placeholder:text-white/60 outline-none focus:bg-white/30 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <button
                onClick={handleCustomFood}
                disabled={!customFood}
                className="px-3 py-2 bg-white/25 text-white rounded-xl text-sm font-semibold disabled:opacity-40 hover:bg-white/35"
              >
                {t('records.toolbar.add')}
              </button>
              {totalFoodIn > 0 && onUndoFood && (
                <button onClick={onUndoFood} className="p-2 hover:bg-white/20 rounded-full text-white/70 transition-colors" title="Undo last food entry">
                  <RotateCcw size={16} />
                </button>
              )}
            </div>
          </div>

        </div>
      )}

      {/* ── Diary expansion panel ── */}
      {diaryOpen && (
        <div className="px-4 pt-4 pb-3 bg-primary-brown border-b border-white/20 space-y-3">

          {/* Mood picker */}
          <div>
            <p className="text-xs font-bold text-white/80 uppercase tracking-widest mb-2">
              How’s {petName} feeling today?
            </p>
            <div className="flex gap-1.5">
              {MOODS.map((icon, i) => (
                <button
                  key={i}
                  type="button"
                  onMouseDown={e => { e.preventDefault(); setDiaryMood(m => m === i ? null : i); }}
                  className={cn(
                    'flex-1 flex items-center justify-center py-1.5 rounded-xl border-2 transition-all text-white',
                    diaryMood === i
                      ? 'border-white bg-white/30 scale-105 shadow-sm'
                      : 'border-white/20 hover:bg-white/15'
                  )}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>

          {/* Editor */}
          <DiaryEditor
            content={diaryInput}
            onChange={setDiaryInput}
            placeholder={t('medcard.diary.placeholder')}
          />

          {/* Save */}
          <button
            onClick={handleDiarySave}
            disabled={!diaryInput || diaryInput === '<p></p>'}
            className="w-full py-2 bg-white/25 text-white rounded-xl text-sm font-semibold disabled:opacity-40 hover:bg-white/35 transition-colors"
          >
            {t('records.toolbar.save')}
          </button>

          {/* Past entries stream */}
          {diaryEntries.length > 0 && (
            <div className="pt-2 border-t border-white/20 space-y-2.5 max-h-72 overflow-y-auto pr-1">
              {diaryEntries.map(entry => (
                <div key={entry.id} className="bg-white/10 rounded-xl p-3 space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] font-semibold text-white/50 leading-tight">
                      {format(new Date(entry.createdAt), "EEE d MMM yyyy '·' HH:mm")}
                    </span>
                    {entry.mood !== null && (
                      <span className="text-white/80 flex-shrink-0">{MOODS[entry.mood]}</span>
                    )}
                  </div>
                  <div
                    className="text-sm text-white/90 leading-relaxed [&_p]:my-0.5 [&_ul]:pl-4 [&_ul]:list-disc [&_ol]:pl-4 [&_ol]:list-decimal [&_strong]:font-bold [&_em]:italic"
                    dangerouslySetInnerHTML={{ __html: entry.html }}
                  />
                </div>
              ))}
            </div>
          )}

        </div>
      )}

      {/* ── Toolbar pill row ── */}
      <div className="flex divide-x divide-primary-brown/30 bg-primary-brown">
        <button
          onClick={() => toggle('wellness')}
          className={cn(
            'flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold transition-colors',
            wellnessOpen ? 'bg-black/20 text-white' : 'text-white/80 hover:bg-black/10'
          )}
        >
          <HouseHeart className="w-4 h-4" />
          {t('records.wellnessCheck')}
          <ChevronDown className={cn('w-3 h-3 transition-transform', wellnessOpen && 'rotate-180')} />
        </button>

        <button
          onClick={() => toggle('diary')}
          className={cn(
            'flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold transition-colors',
            diaryOpen ? 'bg-black/20 text-white' : 'text-white/80 hover:bg-black/10'
          )}
        >
          <BookOpen className="w-4 h-4" />
          {t('medcard.diary.title')}
          <ChevronDown className={cn('w-3 h-3 transition-transform', diaryOpen && 'rotate-180')} />
        </button>
      </div>

    </div>
  );
}
